"use client";

import { useMemo } from "react";
import { cn } from "@ui/lib";
import { generateHeatmapData } from "../lib/statistics";

const QUALITY_STYLES: Record<string, string> = {
  good: "text-emerald-500",
  ok: "text-amber-500",
  weak: "text-rose-400",
  poor: "text-muted-foreground",
};

const QUALITY_BG: Record<string, string> = {
  good: "bg-emerald-500/10",
  ok: "bg-amber-500/10",
  weak: "bg-rose-500/8",
  poor: "",
};

const PROMPT_COUNTS = [5, 10, 15, 20, 25, 30, 40, 50];

interface HeatmapTableProps {
  p: number;
  conf: number;
}

export function HeatmapTable({ p, conf }: HeatmapTableProps) {
  const data = useMemo(() => generateHeatmapData(p, conf), [p, conf]);

  return (
    <div className="rounded-2xl border bg-card p-6">
      <h3 className="text-lg font-semibold">
        Margin of Error by Prompts &amp; Frequency
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Each cell shows ±margin of error at your selected confidence level and
        observed mention rate. Green = tight &amp; reliable. Yellow = directional. Red
        = exploratory only.
      </p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
                Frequency ↓ / Prompts →
              </th>
              {PROMPT_COUNTS.map((pc) => (
                <th
                  key={pc}
                  className="px-3 py-2 text-center font-semibold text-muted-foreground"
                >
                  {pc}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.frequency} className="border-t border-border/50">
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                  {row.frequency} ({row.runs}×/mo)
                </td>
                {row.cells.map((cell) => (
                  <td
                    key={cell.prompts}
                    className={cn(
                      "px-3 py-2 text-center font-medium",
                      QUALITY_STYLES[cell.quality],
                      QUALITY_BG[cell.quality],
                    )}
                  >
                    ±{cell.me.toFixed(1)}%
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
